import React, { createContext, useContext, useState, useRef, useEffect, useCallback, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { Conversation, ProgressUpdate, UserPreferences } from '../types';
import { PreviewFile } from '../components/CodePreviewPanel';
import { apiClient } from '../services/api';

interface PreviewState {
  files: PreviewFile[];
  conversationId: string;
  isEdit?: boolean;
  editTargetBranch?: string;
  editRepo?: string;
  prTitle?: string;
  prDescription?: string;
  _truncated?: boolean;
}

export interface ModuleStats {
  tfcModulesReused: number;
  moduleNames: string[];
  inlineResources: number;
  resourceTypes: string[];
  newModulesPushed: number;
  pushedModuleNames: string[];
  isEdit: boolean;
}

export interface CompletedGeneration {
  adoLink: string;
  repoName: string;
  branch: string;
  folder: string;
  repoCloneUrl?: string;
  provider?: 'aws' | 'azure' | 'gcp';
  /** Path to tfvars file relative to repo root — set when agent generates IaC with variables/ per PDI standards */
  tfvarsFile?: string;
  /** Path to backend config HCL relative to repo root — set when agent generates IaC with backend-configs/ per PDI standards */
  backendConfigFile?: string;
}

interface AppContextType {
  // Current conversation
  currentConversation: Conversation | null;
  setCurrentConversation: (conversation: Conversation | null) => void;

  // Conversations list
  conversations: Conversation[];
  setConversations: (conversations: Conversation[]) => void;

  // Progress updates
  progressUpdates: ProgressUpdate[];
  addProgressUpdate: (update: ProgressUpdate) => void;
  clearProgressUpdates: () => void;

  // User preferences
  preferences: UserPreferences | null;
  setPreferences: (preferences: UserPreferences) => void;

  // Generation state — per-conv: true only for the conversation that is actively generating
  isGenerating: boolean;
  setIsGenerating: (generating: boolean) => void;
  /** Set isGenerating for a specific conv (cross-conv WS lifecycle events) */
  setIsGeneratingForConv: (convId: string, value: boolean) => void;
  /** Clear all generating flags on page load / auth (stale state cleanup) */
  clearAllGenerating: () => void;

  // Code preview (preview_ready stage) — per-conv: survives New Conversation click
  previewState: PreviewState | null;
  setPreviewState: (state: PreviewState | null) => void;

  // Module usage stats (from last generation)
  moduleStats: ModuleStats | null;
  setModuleStats: (stats: ModuleStats | null) => void;

  // Completed generation — ADO push result, needed to open Jenkins dialog
  completedGeneration: CompletedGeneration | null;
  setCompletedGeneration: (data: CompletedGeneration | null) => void;

  // Sidebar open/close (ChatGPT-style layout)
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;

  // Intermediate streaming text from the agent (set while agent is running, cleared on completion)
  streamingText: string | null;
  setStreamingText: (text: string | null) => void;

  // Conversation list loader — called on mount and after mutations
  loadConversations: () => Promise<void>;

  // Resume a previous conversation by ID — fetches full data and restores state
  resumeConversation: (conversationId: string) => Promise<void>;

  // True once userId has resolved and all persisted state has been re-hydrated
  // from localStorage. Gate any UI that depends on persisted state behind this.
  hydrated: boolean;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

// ─── Per-user localStorage hook ──────────────────────────────────────────────
// Keys are prefixed with `iac:{userId}:` so two different Okta users on the
// same browser never see each other's state.
// localStorage persists across tab close and relogin — state is only cleared
// when the user explicitly clicks "New".
function makeKey(userId: string, name: string): string {
  return `iac:${userId}:${name}`;
}

function readSession<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeSession<T>(key: string, value: T | null | undefined): void {
  if (value === null || value === undefined) {
    localStorage.removeItem(key);
    return;
  }
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // QuotaExceededError — state lives in memory only; UI never breaks
  }
}

// Hook: behaves like useState but syncs to localStorage under a user-scoped key.
// userId may be '' (not yet known) — in that case reads/writes are skipped until
// the user is known, then the hook re-initialises from storage.
function useSessionState<T>(
  userId: string,
  name: string,
  initial: T
): [T, (value: T | null | undefined) => void] {
  const key = userId ? makeKey(userId, name) : '';

  const [state, setState] = useState<T>(() => {
    if (!key) return initial;
    return readSession<T>(key, initial);
  });

  // When userId becomes available (after Okta auth), re-hydrate from storage.
  const prevUserIdRef = useRef('');
  useEffect(() => {
    if (!userId || userId === prevUserIdRef.current) return;
    prevUserIdRef.current = userId;
    const stored = readSession<T>(makeKey(userId, name), initial);
    setState(stored);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const setAndPersist = (value: T | null | undefined) => {
    setState(value as T);
    if (key) writeSession(key, value);
  };

  return [state, setAndPersist];
}

// ─── Provider ─────────────────────────────────────────────────────────────────
export const AppProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authState, oktaAuth } = useOktaAuth();
  const [userId, setUserId] = useState('');
  // hydrated flips to true once userId resolves and all state has been read
  // from localStorage. Gate any UI that depends on persisted state behind this
  // to prevent a flash of the wrong panel (e.g. chat instead of preview).
  const [hydrated, setHydrated] = useState(false);

  // ── Persisted state ──────────────────────────────────────────────────────
  const [currentConversation, setCurrentConversation] = useSessionState<Conversation | null>(
    userId, 'conversation', null
  );
  // Progress updates are stored per-conversation so switching sessions shows only
  // that conversation's progress. Key = conversationId, value = ProgressUpdate[].
  const [allProgress, setAllProgress] = useSessionState<Record<string, ProgressUpdate[]>>(
    userId, 'progress', {}
  );
  // Per-conv generating flags: key = conversationId, value = true if that conv is generating
  const [allGenerating, _setAllGenerating] = useSessionState<Record<string, boolean>>(
    userId, 'generating', {}
  );
  // Per-conv preview state: key = conversationId, value = PreviewState (or absent if none)
  const [allPreviews, _setAllPreviews] = useSessionState<Record<string, PreviewState | null>>(
    userId, 'previews', {}
  );
  const [moduleStats, setModuleStats] = useSessionState<ModuleStats | null>(
    userId, 'modulestats', null
  );
  const [completedGeneration, setCompletedGeneration] = useSessionState<CompletedGeneration | null>(
    userId, 'completed-generation', null
  );

  useEffect(() => {
    if (authState?.isAuthenticated) {
      oktaAuth.getUser().then((u) => {
        if (u?.sub) setUserId(u.sub);
      }).catch(() => {});
    } else if (authState && !authState.isAuthenticated) {
      setUserId('');
      setHydrated(false);
    }
  }, [authState, oktaAuth]);

  // Once userId is known, all useSessionState hooks re-read from localStorage
  // in their own useEffects. We need hydrated=true only AFTER those reads have
  // been committed to state. We do this by watching userId in a separate effect
  // with a setTimeout(0) — this runs after all same-tick useEffects have fired
  // and React has re-rendered with the hydrated values.
  const prevUserIdRef = useRef('');
  useEffect(() => {
    if (!userId || userId === prevUserIdRef.current) return;
    prevUserIdRef.current = userId;
    // setTimeout(0) defers until after React has processed all the useSessionState
    // useEffect re-hydrations triggered by the same userId change.
    const t = setTimeout(() => setHydrated(true), 0);
    return () => clearTimeout(t);
  }, [userId]);

  // ── Non-persisted state ──────────────────────────────────────────────────
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [streamingText, setStreamingText] = useState<string | null>(null);

  // ── progressUpdates: derive flat array for current conversation ─────────
  const currentConvId = currentConversation?.conversationId ?? '';
  const progressUpdates = allProgress[currentConvId] ?? [];

  // Refs for stale-closure-safe access inside the WS subscriber (registered once).
  const allProgressRef = useRef(allProgress);
  useEffect(() => { allProgressRef.current = allProgress; }, [allProgress]);

  const currentConvIdRef = useRef(currentConvId);
  useEffect(() => { currentConvIdRef.current = currentConvId; }, [currentConvId]);

  // Refs for per-conv maps
  const allGeneratingRef = useRef(allGenerating);
  useEffect(() => { allGeneratingRef.current = allGenerating; }, [allGenerating]);

  const allPreviewsRef = useRef(allPreviews);
  useEffect(() => { allPreviewsRef.current = allPreviews; }, [allPreviews]);

  // Derived: current conv's values
  const isGenerating = allGenerating[currentConvId] ?? false;
  const previewState = allPreviews[currentConvId] ?? null;

  const setIsGenerating = (value: boolean) => {
    const convId = currentConvIdRef.current;
    if (!convId) return;
    const updated = { ...allGeneratingRef.current };
    if (!value) delete updated[convId]; else updated[convId] = true;
    _setAllGenerating(updated);
  };

  const setIsGeneratingForConv = (convId: string, value: boolean) => {
    if (!convId) return;
    const updated = { ...allGeneratingRef.current };
    if (!value) delete updated[convId]; else updated[convId] = true;
    _setAllGenerating(updated);
  };

  const clearAllGenerating = () => {
    _setAllGenerating({});
  };

  const setPreviewState = (state: PreviewState | null) => {
    const updated = { ...allPreviewsRef.current };
    if (state === null) {
      const convId = currentConvIdRef.current;
      if (convId) delete updated[convId];
    } else {
      const targetId = state.conversationId || currentConvIdRef.current;
      if (targetId) updated[targetId] = state;
    }
    _setAllPreviews(updated);
  };

  const addProgressUpdate = (update: ProgressUpdate) => {
    // Route to the conversation the event belongs to (from backend tag), falling
    // back to the current conversation for legacy events without conversationId.
    const convId = update.conversationId || currentConvIdRef.current;
    if (!convId) return; // no target conversation — drop the update
    const current = allProgressRef.current[convId] ?? [];
    const next = [...current, update].slice(-50); // cap at 50 per conversation
    const updated = { ...allProgressRef.current, [convId]: next };
    setAllProgress(updated);
  };

  const clearProgressUpdates = () => {
    const convId = currentConvIdRef.current;
    if (!convId) {
      setAllProgress({});
      return;
    }
    const updated = { ...allProgressRef.current };
    delete updated[convId];
    setAllProgress(updated);
  };

  const loadConversations = useCallback(async () => {
    try {
      const convs = await apiClient.getConversations();
      setConversations(convs);
    } catch (err) {
      console.error('[AppContext] Failed to load conversations:', err);
    }
  }, []);

  const resumeConversation = useCallback(async (conversationId: string) => {
    try {
      const fullConv = await apiClient.getConversation(conversationId);
      setCurrentConversation(fullConv);
      // Do NOT wipe previewState or progressUpdates here.
      // - previewState must stay visible so the user can review and push to ADO.
      // - progressUpdates (and moduleStats derived from them) must stay so the
      //   Module Usage panel in ProgressDisplay remains visible during preview.
      // Both are cleared only by explicit user actions (push, cancel, new conversation)
      // or by the completed WS handler when adoLink/isEdit is present.
    } catch (err) {
      console.error('[AppContext] Failed to resume conversation:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppContext.Provider
      value={{
        currentConversation,
        setCurrentConversation,
        conversations,
        setConversations,
        progressUpdates,
        addProgressUpdate,
        clearProgressUpdates,
        preferences,
        setPreferences,
        isGenerating,
        setIsGenerating,
        setIsGeneratingForConv,
        clearAllGenerating,
        previewState,
        setPreviewState,
        moduleStats,
        setModuleStats,
        completedGeneration,
        setCompletedGeneration,
        sidebarOpen,
        setSidebarOpen,
        streamingText,
        setStreamingText,
        loadConversations,
        resumeConversation,
        hydrated,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

export const useApp = (): AppContextType => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};
